import { difference } from 'es-toolkit';

import { SealStatus } from '@paragon/contract-library';
import { getChildrenFinder, getComponentDesignDescendantPaths, PathUtilities } from '@paragon/layout-models';

import { getSealingInformation } from '../selectors/sealingInformation';
import { getAllInformationForSealedStatus } from './componentDesignSealTimestamps';
import { checkRows, collapseRow, expandRow, expandRows, uncheckRows } from './grid';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const toggleProjectGridRowExpansion =
  (rowPath: string): ThunkActionVoid =>
  (dispatch, getState) => {
    if (getState().grid.expandedRows.includes(rowPath)) {
      dispatch(collapseRow(rowPath));
      return;
    }

    dispatch(expandRow(rowPath));

    if (PathUtilities.isProject(rowPath)) {
      void dispatch(getAllInformationForSealedStatus(rowPath));
    }
  };

export const checkComponentDesignsBySealStatus =
  (projectGuid: string, sealStatus: SealStatus): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let checkedDuringFirstIteration: string[] | null = null;
    // We want the user interface to be responsive, but we also want the check buttons to be accurate regardless of how
    //   old the data is. Therefore, we check rows once based on the current data (if available), and then do so again
    //   after the data has been updated. Because the user might have checked or unchecked rows in the meantime, we need
    //   to keep track of which were checked during the first iteration to avoid rechecking them.
    const checkRowsBasedOnCurrentData = () => {
      const state = getState();
      let rowsToCheck = getComponentDesignDescendantPaths(projectGuid, getChildrenFinder(state)).filter(
        (path) => getSealingInformation(state, PathUtilities.getGuid(path)).sealStatus === sealStatus,
      );

      if (checkedDuringFirstIteration) {
        const wronglyChecked = difference(checkedDuringFirstIteration, rowsToCheck);
        dispatch(uncheckRows(wronglyChecked));
        // The user might have checked a row in the meantime, so avoid rechecking it.
        rowsToCheck = difference(rowsToCheck, checkedDuringFirstIteration);
      } else {
        checkedDuringFirstIteration = rowsToCheck;
      }

      dispatch(expandRows(rowsToCheck.flatMap((path) => [...PathUtilities.iterateUpAncestors(path)])));
      dispatch(checkRows(rowsToCheck));
    };

    // If the project is already expanded, we know some data exists for it already (although it may be outdated), and
    //   we can initially check rows using that.
    if (getState().grid.expandedRows.includes(projectGuid)) checkRowsBasedOnCurrentData();
    // If not, it needs to be expanded now so that the user can see the loading indicators.
    else dispatch(expandRow(projectGuid));

    await dispatch(getAllInformationForSealedStatus(projectGuid));

    // After fresh data is loaded, we call this again to update only those wrongly checked or ignored during the
    //   first iteration (if applicable).
    checkRowsBasedOnCurrentData();
  };
